import { createStore, useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '../../utilities'
import { ThankYouScreen } from '../../views'
import {
	ScreenChangeInfo,
	thankYouScreenChangeCallbackAtom,
	thankYouScreenDebounceValueAtom,
	thankYouScreenLifecycleAtom,
	thankYouScreenResetAllCallbackAtom,
	thankYouScreenResetCallbackAtom,
	thankYouScreensAtom,
	thankYouScreenSavingAtom,
	useThankYouScreensListener,
} from './thank-you-screen-atoms'

export type UseThankYouScreenLibraryArgs = {
	screens?: ThankYouScreen[]
	isPending?: boolean
	isGenerating?: boolean
	onSave?: (screens: ThankYouScreen[]) => Promise<void>
	onScreenChange?: (info: ScreenChangeInfo) => void
	onResetScreen?: (screen: ThankYouScreen) => Promise<void>
	onResetAllScreens?: () => Promise<void>
	version?: string | number | Date | null
}

export function useThankYouScreenLibrary({
	screens = [],
	isPending = false,
	isGenerating = false,
	onSave,
	onScreenChange,
	onResetScreen,
	onResetAllScreens,
	version = null,
}: UseThankYouScreenLibraryArgs) {
	const store = useMemo(() => createStore(), [])

	useEffect(() => {
		store.set(thankYouScreenChangeCallbackAtom, () => onScreenChange ?? null)
	}, [store, onScreenChange])

	useEffect(() => {
		store.set(thankYouScreenResetCallbackAtom, () => onResetScreen ?? null)
	}, [store, onResetScreen])

	useEffect(() => {
		store.set(thankYouScreenResetAllCallbackAtom, () => onResetAllScreens ?? null)
	}, [store, onResetAllScreens])

	const thankYouScreensDebounceValue = useAtomValue(thankYouScreenDebounceValueAtom)

	const lifecycle = useLifecyclePersistence<ThankYouScreen[]>({
		data: screens,
		isIdle: (d) => d === undefined || d.length === 0,
		isPending: () => isPending,
		isGenerating: () => isGenerating,
		save: async (data) => {
			await onSave?.(data)
		},
		debounceMs: thankYouScreensDebounceValue,
		getVersion: () => version,
		debug: false,
		name: 'useThankYouScreenLibrary',
	})

	useSyncLifecycleToExistingAtom<ThankYouScreen[]>(
		lifecycle,
		{
			dataAtom: thankYouScreensAtom,
			useDataListener: useThankYouScreensListener,
			lifecycleAtom: thankYouScreenLifecycleAtom,
			persistenceAtom: thankYouScreenSavingAtom,
		},
		{ store },
	)

	return { store }
}
